"use client";

import { useSession } from "@/contexts/sessionContext";
import { useToast } from "@/hooks/useToast";
import { logger } from "@/modules/lib/observability/logger";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { ZendeskContext } from "./ZendeskContext";
import { destroyZendeskSDK, initializeZendeskSDK } from "./initializeSDK";
import type {
  ZendeskContextType,
  ZendeskConversationField,
  ZendeskProviderProps,
} from "./types";

export const ZendeskProvider: React.FC<ZendeskProviderProps> = ({
  children,
  widgetId,
  lineId,
  locale,
}) => {
  const { addToast } = useToast();
  const t = useTranslations("support.toast");
  const [isZendeskLoaded, setIsZendeskLoaded] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useSession();
  const zendeskToken = user?.supportToken;
  const prevUnreadMessages = useRef<number>(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (unreadMessages > prevUnreadMessages.current) {
      addToast({
        type: "message",
        title: t("title"),
        message: t("description", { messageCount: unreadMessages }),
        onClick: () => {
          openChat();
          return "";
        },
      });
    }
  }, [unreadMessages]);

  function openChat() {
    try {
      window.zE?.("messenger", "open");
    } catch (e) {
      setIsZendeskLoaded(false);
      logger.error("Failed to trigger Zendesk SDK command", {}, e as Error);
    }
  }

  function closeChat() {
    try {
      window.zE?.("messenger", "hide");
    } catch (e) {
      setIsZendeskLoaded(false);
      logger.error("Failed to trigger Zendesk SDK command", {}, e as Error);
    }
  }

  const hasDigitalLine = !!lineId;

  function callDigitalLine() {
    if (!lineId) return;
    try {
      window.zE?.("messenger:open", "voice", lineId);
    } catch (e) {
      setIsZendeskLoaded(false);
      logger.error("Failed to trigger Zendesk SDK command", {}, e as Error);
    }
  }

  function setConversationalFields(fields: ZendeskConversationField[]) {
    if (!isZendeskLoaded) return;
    try {
      window.zE?.("messenger:set", "conversationFields", fields);
    } catch (e) {
      setIsZendeskLoaded(false);
      logger.error("Failed to trigger Zendesk SDK command", {}, e as Error);
    }
  }

  function setConversationalTags(tags: string[]) {
    if (!isZendeskLoaded) return;
    try {
      window.zE?.("messenger:set", "conversationTags", tags);
    } catch (e) {
      setIsZendeskLoaded(false);
      logger.error("Failed to trigger Zendesk SDK command", {}, e as Error);
    }
  }

  function logout() {
    if (!isZendeskLoaded) return;
    try {
      window.zE?.("messenger", "logoutUser");
    } catch (e) {
      setIsZendeskLoaded(false);
      logger.error("Failed to trigger Zendesk SDK command", {}, e as Error);
    }
  }

  const contextValues: ZendeskContextType = {
    isZendeskLoaded,
    unreadMessages,
    openChat,
    closeChat,
    isOpen,
    hasDigitalLine,
    callDigitalLine,
    setConversationalFields,
    setConversationalTags,
    logout,
  };

  function handleSDKLoaded() {
    /* 
      This function is called when the Zendesk SDK script is loaded
      Note: Even if loaded, the SDK might not be fully initialized yet. Example if widgetId is invalid or account expired.
    */
    setIsZendeskLoaded(true);

    try {
      if (locale) {
        window.zE?.("messenger:set", "locale", locale);
      }
      // ToDo: Adjust cookie usage based on user consent
      // window.zE?.("messenger:set", "cookies", "all");

      if (zendeskToken) {
        window.zE?.("messenger", "loginUser", (cb) => {
          cb(zendeskToken);
        });
      }

      // Setup basic event listeners
      window.zE?.("messenger:on", "open", () => {
        // ToDo: Add event tracking
        setIsOpen(true);
      });

      window.zE?.("messenger:on", "close", () => {
        setIsOpen(false);
      });

      window.zE?.("messenger:on", "unreadMessages", (count: number) => {
        setUnreadMessages(count);
      });
    } catch (e) {
      setIsZendeskLoaded(false);
      logger.error("Failed to initialize Zendesk SDK", {}, e as Error);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: handleSDKLoaded is a immutable function
  useEffect(() => {
    if (!widgetId) return;
    initializeZendeskSDK(widgetId, handleSDKLoaded);
    return () => {
      /* 
        AQ-1241
        This is a workaround for the issue where there is a layour change
        The SDK is preesnt in JS (via window.zE object)
        but all related DOM was removed by NextjS routing.
      */
      destroyZendeskSDK();
    };
  }, [widgetId]);

  return (
    <ZendeskContext.Provider value={contextValues}>
      {children}
    </ZendeskContext.Provider>
  );
};
